// Hex lattice construction.
//
// Every vertex is a 3-way Y-junction (three edges at 120°).
// Every edge has equal length. The grid wraps as a torus.
// See game-design-doc §4.
//
// Construction uses a two-sublattice (honeycomb) model:
//   Per hex cell (q, r): two vertex types, A (id parity 0) and B (id parity 1).
//   Per hex cell (q, r): three edge types (vertical, horizontal, diagonal).
//   Total vertices: 2 * cols * rows
//   Total edges:    3 * cols * rows
// Euler characteristic of torus: V - E + F = 0 ⟹ 2n - 3n + n = 0 ✓

import type { Axial, WorldDims } from './coords.js';

export type VertexId = number;
export type EdgeId = number;

export interface Vertex {
  id: VertexId;
  pos: Axial;
  /** Exactly three edges meet here. */
  edges: [EdgeId, EdgeId, EdgeId];
}

export interface Edge {
  id: EdgeId;
  from: VertexId;
  to: VertexId;
}

export interface HexGrid {
  dims: WorldDims;
  vertices: Vertex[];
  edges: Edge[];
}

/** Construct a torus hex lattice of the given dimensions. */
export function buildGrid(dims: WorldDims): HexGrid {
  const { cols, rows } = dims;

  // --- ID helpers ---
  // vid(q, r, t): t=0 → A-type vertex, t=1 → B-type vertex.
  // IDs are contiguous in [0, 2*cols*rows) so vertices[id] is an O(1) lookup.
  const vid = (q: number, r: number, t: 0 | 1): VertexId =>
    (r * cols + q) * 2 + t;

  // eid(q, r, t): one edge per cell per type; IDs contiguous in [0, 3*cols*rows).
  //   t=0: vertical   A(q,r) ↔ B(q,r)
  //   t=1: horizontal A(q,r) ↔ B((q-1+cols)%cols, r)
  //   t=2: diagonal   A(q,r) ↔ B(q, (r-1+rows)%rows)
  const eid = (q: number, r: number, t: 0 | 1 | 2): EdgeId =>
    (r * cols + q) * 3 + t;

  const vertices: Vertex[] = [];
  const edges: Edge[] = [];

  for (let r = 0; r < rows; r++) {
    for (let q = 0; q < cols; q++) {
      // --- Three edges anchored at A(q,r) ---
      edges.push({ id: eid(q, r, 0), from: vid(q, r, 0), to: vid(q, r, 1) });
      edges.push({
        id: eid(q, r, 1),
        from: vid(q, r, 0),
        to: vid((q - 1 + cols) % cols, r, 1),
      });
      edges.push({
        id: eid(q, r, 2),
        from: vid(q, r, 0),
        to: vid(q, (r - 1 + rows) % rows, 1),
      });

      // --- A-type vertex (q, r) ---
      // Its three incident edges are exactly eid(q,r,0..2).
      vertices.push({
        id: vid(q, r, 0),
        pos: { q: q * 2, r: r * 2 },
        edges: [eid(q, r, 0), eid(q, r, 1), eid(q, r, 2)],
      });

      // --- B-type vertex (q, r) ---
      // Three A-cells point to B(q,r):
      //   A(q,r)          via eid(q,r,0)              (vertical)
      //   A((q+1)%cols,r) via eid((q+1)%cols,r,1)     (horizontal, wraps left)
      //   A(q,(r+1)%rows) via eid(q,(r+1)%rows,2)     (diagonal, wraps up)
      vertices.push({
        id: vid(q, r, 1),
        pos: { q: q * 2 + 1, r: r * 2 + 1 },
        edges: [
          eid(q, r, 0),
          eid((q + 1) % cols, r, 1),
          eid(q, (r + 1) % rows, 2),
        ],
      });
    }
  }

  vertices.sort((a, b) => a.id - b.id);
  edges.sort((a, b) => a.id - b.id);

  return { dims, vertices, edges };
}
